import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "preact/jsx-runtime";
/* eslint-disable max-len,@typescript-eslint/naming-convention */
import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState, } from 'preact/compat';
import { addMonths, eachMonthOfInterval, endOfMonth, format, isPast, isToday, parse, startOfMonth, } from 'date-fns';
import { useController, useFormContext, } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import useDidMount from 'beautiful-react-hooks/useDidMount';
import clsx from 'clsx';
import API from '@widgets/shared/API/connection';
import formatAvailableDates from '@widgets/shared/formatAvailableDates';
import DatesCTX from '@widgets/shared/components/CustomDatepicker/context/dates';
import useLowAvailabilityDates from '@widgets/GenericBookings/components/CustomDatepicker/hooks/useLowAvailabilityDates';
import useDatepickerConfig from '@widgets/GenericBookings/components/CustomDatepicker/hooks/useDatepickerConfig';
import DatepickerDay from '@widgets/GenericBookings/components/CustomDatepicker/components/DatepickerDay';
import { DefaultHeader } from '@widgets/GenericBookings/components/CustomDatepicker/components/headers';
import LoadingDates from '@widgets/GenericBookings/components/CustomDatepicker/components/LoadingDates';
import defaultNumberFormat, { winterWonderlandNumberFormat } from '@widgets/shared/defaultNumberFormat';
import { eachDayOfInterval } from 'date-fns/fp';
import { useGesture } from '@use-gesture/react';
import AvailabilityKey from '@genericBookings/components/CustomDatepicker/components/AvailabilityKey';
import MediaSizes from '@widgets/shared/MediaSizes';
import { useStepCompletion } from '@widgets/DayTickets/context/Steps';
import useQueryParams from '@widgets/DIYDayTickets/hooks/useQueryParams';
import { getActivities, getActivitiesForDate, getFetchingStatus, readProductDetails, } from '@genericBookings/store/reducers/activities';
import { useAppDispatch, useAppSelector } from '@genericBookings/store/hooks';
import { visitDateSelectedEvent } from '@genericBookings/primaryActionEvents';
import { useFieldsWrapper } from '@genericBookings/context/FieldsWrapper';
import defaultDatepickerArgs from './defaults';
import { CalendarKeyWrapper, CalendarWrapper, DatePickerStyles, swwCalendarWidthWithPadding, Timeline, TimelineItem, } from './styles';
import SWWArrow from './components/SWWArrow';
function CustomDatepicker(props) {
    // Set up react-hook-form
    const { name: fieldName, options, shownDate, requiresVisitorsCheck, calendarOptions, availabilities: initialAvailabilities, productId, layout, } = props;
    const { fromDate, toDate, showPricesOnCalendar, analyticsTitle, analyticsPath, analyticsUrl, showAvailability, isMulti, isBuilder, selectedDate: initialSelectedDate, priceBandId, calendarStartDay, weekDayFormat, } = useContext(options);
    const { modifyStep, dateStep, } = useStepCompletion();
    const { visitDate: paramVisitDate, } = useQueryParams(options);
    const dispatch = useAppDispatch();
    const activities = useAppSelector(getActivities);
    const { fetchedProductBands, fetchedPriceBands } = useAppSelector(getFetchingStatus);
    const { setValue } = useFormContext();
    const handleDidMount = useCallback(async () => {
        // navigate to next step if it's included in the query params.
        if (paramVisitDate || initialSelectedDate) {
            if (!isBuilder && !isMulti) {
                await dispatch(readProductDetails({
                    activities,
                    date: paramVisitDate ?? initialSelectedDate ?? new Date().toISOString(),
                }));
            }
            else if (isBuilder) {
                await dispatch(getActivitiesForDate({
                    productId,
                    date: paramVisitDate ?? initialSelectedDate ?? new Date().toISOString(),
                }));
            }
            setValue('VisitDate', (paramVisitDate ?? initialSelectedDate ?? new Date().toISOString()));
            modifyStep(dateStep, true, true);
        }
        // await dispatch(readPrices({ activities: [currActivity], date }));
    }, [paramVisitDate, initialSelectedDate, dateStep, isBuilder, isMulti, setValue, modifyStep, dispatch, activities, productId]);
    useEffect(() => {
        if (!fetchedProductBands && !fetchedPriceBands && activities && dateStep && !dateStep.isViewed) {
            handleDidMount();
        }
        // probably not the best way of doing it, but if the initial selected date is set, this is probably from a modal
        else if (initialSelectedDate && activities && dateStep && !dateStep.isViewed) {
            // handleDidMount();
        }
    }, [fetchedProductBands, fetchedPriceBands, activities, dateStep, handleDidMount, initialSelectedDate]);
    // useDidMount(handleDidMount);
    const { control, getValues } = useFormContext();
    const { field: { value: fieldValue },
    // fieldState: { error, invalid, isTouched, },
     } = useController({
        name: fieldName,
        control,
    });
    const ref = useRef(null);
    // Context/State var
    // Refs
    const loadingRef = useRef(null);
    // Side Effects
    const prefix = useId();
    const onMount = useDidMount();
    const [selectedDate, setSelectedDate] = useState(fieldValue ? parse(fieldValue, 'yyyy-MM-dd', new Date()) : null);
    const [formattedAvailableDates, setFormattedAvailableDates] = useState([]);
    const [availablePrices, setAvailablePrices] = useState([]);
    const [queriedMonths, setQueriedMonths] = useState([]);
    const [isWorking, setIsWorking] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(10);
    const lowAvailability = useMemo(() => initialAvailabilities.filter(({ availability }) => availability === 'low').map(({ date }) => date), [initialAvailabilities]);
    const highAvailability = useMemo(() => initialAvailabilities.filter(({ availability }) => availability === 'high').map(({ date }) => date), [initialAvailabilities]);
    const averageAvailability = useMemo(() => initialAvailabilities.filter(({ availability }) => availability === 'average').map(({ date }) => date), [initialAvailabilities]);
    const soldOut = useMemo(() => initialAvailabilities.filter(({ availability }) => availability === 'sold-out').map(({ date }) => date), [initialAvailabilities]);
    const { setCalendarHighlighted, calendarHighlighted } = useFieldsWrapper();
    const highlightedClass = useMemo(() => (calendarHighlighted ? ['highlighted'] : []), [calendarHighlighted]);
    const { lowAvailabilityDates, highAvailabilityDates, averageAvailabilityDates, soldOutDates } = useLowAvailabilityDates(lowAvailability ?? [], highAvailability ?? [], averageAvailability ?? [], soldOut ?? []);
    const { isWinterWonderland, monthsShown, isAfterNov, isAfterDec, minDate, maxDate, usedYear, } = useDatepickerConfig({
        formattedAvailableDates,
        fromDate: fromDate ?? format(new Date(), 'yyyy-MM-dd'),
        toDate: toDate ?? format(addMonths(new Date(), 2), 'yyyy-MM-dd'),
        layout: layout ?? 'winter-wonderland',
        options,
    });
    const calendarClasses = useMemo(() => clsx('datepicker-wrappers', highlightedClass, `dates--${monthsShown ?? '0'}`), [highlightedClass, monthsShown]);
    // return null;
    const handleChange = (newValue) => {
        setSelectedDate(newValue);
    };
    const returnOpenToDate = () => {
        if (shownDate) {
            return shownDate;
        }
        return minDate;
    };
    const renderHeaderContent = (headerProps) => _jsx(DefaultHeader, { ...headerProps });
    const filterDates = (date) => {
        // sold-out dates are shown but never selectable, regardless of any returned price.
        if (soldOutDates.includes(format(date, 'yyyy-MM-dd')))
            return false;
        const dateStr = format(date, 'yyyyMMdd');
        const includesDate = formattedAvailableDates.includes(dateStr);
        return isWinterWonderland ? includesDate && (!isPast(date) || isToday(date)) : includesDate;
    };
    const getDayClassName = useCallback((date) => (soldOutDates.includes(format(date, 'yyyy-MM-dd')) ? 'sold-out' : ''), [soldOutDates]);
    const getData = useCallback(async (fromDateString, toDateString, fromDate) => {
        const visitorObject = {};
        // if we ever need to re-add this then add to ActiveActivities as an optional param...
        if (requiresVisitorsCheck) {
            for (let i = 0; i < 11; i++) {
                const key = `Visitors${i}`;
                const unformattedValue = getValues(key);
                const formattedValue = parseFloat(`${unformattedValue}`);
                if (formattedValue > 0) {
                    visitorObject[key] = formattedValue;
                }
            }
        }
        if (showPricesOnCalendar && priceBandId && priceBandId !== 0) {
            const dates = await API.dayPrices(productId, priceBandId ?? 0, fromDateString, toDateString, visitorObject);
            return {
                dates: formatAvailableDates(Object.values(dates ?? [])
                    .map((date) => date.VisitDateString)),
                prices: Object.values(dates ?? [])
                    .map((date) => ({
                    date: date.VisitDateString,
                    price: isWinterWonderland
                        ? winterWonderlandNumberFormat.format(parseFloat(date.MinPrice))
                        : defaultNumberFormat.format(parseInt(date.MinPrice, 10)),
                })),
            };
        }
        if (isMulti && !isBuilder) {
            setIsWorking(true);
            const res = await API.hasDaySessions(productId, fromDateString, toDateString, visitorObject);
            setIsWorking(false);
            return { dates: formatAvailableDates(res ?? []) };
        }
        if (!isMulti) {
            setIsWorking(true);
            const res = await API.hasSessions(productId, fromDateString, toDateString, visitorObject);
            setIsWorking(false);
            return { dates: formatAvailableDates(res ?? []) };
        }
        // if no product/day ticket is set then get all dates between current month.
        if (fromDate) {
            const dates = eachDayOfInterval({
                start: startOfMonth(fromDate),
                end: endOfMonth(fromDate),
            });
            const formattedDates = dates.map((date) => format(date, 'yyyy-MM-dd'));
            return { dates: formatAvailableDates(formattedDates) };
        }
        return { dates: [] };
    }, [requiresVisitorsCheck, showPricesOnCalendar, isMulti, isBuilder, getValues, productId, priceBandId, isWinterWonderland]);
    const setData = useCallback((data, monthYearKeys = []) => {
        setQueriedMonths(Array.from(new Set([...queriedMonths, ...monthYearKeys])));
        if (data.prices) {
            setAvailablePrices(Array.from(new Set([...availablePrices, ...data.prices])));
        }
        setFormattedAvailableDates(Array.from(new Set([...formattedAvailableDates, ...data.dates])));
    }, [availablePrices, formattedAvailableDates, queriedMonths]);
    const initFirstDatesLoad = useCallback(async () => {
        setIsWorking(true);
        let monthYearKeys;
        let fromDateObj = format(minDate, 'yyyy-MM-dd');
        let toDateObj = format(maxDate, 'yyyy-MM-dd');
        const year = minDate.getFullYear();
        if (isWinterWonderland) {
            monthYearKeys = [`${year}-10`, `${year}-11`, `${year}-12`];
        }
        else {
            const date = shownDate || minDate;
            fromDateObj = format(startOfMonth(date), 'yyyy-MM-dd');
            toDateObj = format(endOfMonth(date), 'yyyy-MM-dd');
            monthYearKeys = [format(date, 'yyyy-MM')];
        }
        const data = await getData(fromDateObj, toDateObj, shownDate || minDate);
        setData(data, monthYearKeys);
        if (requiresVisitorsCheck && loadingRef.current?.childFunction) {
            loadingRef.current?.childFunction()
                .then(() => {
                setIsWorking(false);
            });
        }
        else {
            setIsWorking(false);
        }
    }, [minDate, maxDate, isWinterWonderland, getData, shownDate, setData, requiresVisitorsCheck]);
    const handleRefreshDates = useCallback(() => initFirstDatesLoad(), [initFirstDatesLoad]);
    const handleMonthChanged = async (date) => {
        const monthYearKey = format(startOfMonth(date), 'yyyy-MM');
        const month = date.getUTCMonth();
        if (!queriedMonths.includes(monthYearKey)) {
            const fDate = format(startOfMonth(date), 'yyyy-MM-dd');
            const tDate = format(endOfMonth(date), 'yyyy-MM-dd');
            setIsWorking(true);
            const data = await getData(fDate, tDate, date);
            setData(data, [monthYearKey]);
            if (requiresVisitorsCheck && loadingRef.current?.childFunction) {
                // if (loadingRef.current?.childFunction) {
                loadingRef.current?.childFunction()
                    .then(() => {
                    setIsWorking(false);
                });
            }
            else {
                setIsWorking(false);
            }
        }
        setCurrentMonth(month);
    };
    const handleGtag = useCallback(() => {
        if (typeof gtag !== 'undefined') {
            gtag('event', 'page_view', {
                page_title: `${analyticsTitle} - Select a date`,
                page_location: `${analyticsUrl}step-1`,
                page_path: `${analyticsPath}step-1`,
                send_to: 'UA-44012490-1',
            });
        }
    }, [analyticsPath, analyticsTitle, analyticsUrl]);
    const handleDateSelected = useCallback(async () => {
        if (selectedDate) {
            if (ref.current) {
                const sections = ref.current.closest('.section');
                if (sections) {
                    const el = sections.querySelector('.pick-a-day');
                    if (el) {
                        el.style.display = 'none';
                    }
                }
            }
            handleGtag();
            const formattedDate = format(selectedDate, 'yyyy-MM-dd');
            setValue('VisitDate', formattedDate, { shouldTouch: true });
            if (isBuilder) {
                await dispatch(getActivitiesForDate({
                    productId, date: formattedDate,
                }));
                // TODO: Check if reading product details is needed here.
            }
            else if (!isMulti && !isBuilder) {
                await dispatch(readProductDetails({
                    activities,
                    date: formattedDate,
                }));
            }
            visitDateSelectedEvent();
            modifyStep(dateStep, true, true);
            setCalendarHighlighted(false);
        }
    }, [productId, analyticsPath, analyticsTitle, analyticsUrl, fieldName, selectedDate]);
    useEffect(() => { handleDateSelected(); }, [selectedDate, handleDateSelected]);
    onMount(handleRefreshDates);
    const className = useMemo(() => clsx([
        {
            'single-datepicker': monthsShown < 2,
            'has-low-availability': (lowAvailabilityDates.length ?? 0) > 0 && showAvailability,
        },
        `current-month-${currentMonth}`,
    ]), [monthsShown, lowAvailabilityDates, showAvailability, currentMonth]);
    // day-name label formatter, configurable via the `weekDayFormat` option.
    const formatWeekDay = useMemo(() => {
        switch (weekDayFormat) {
            case 'narrow':
                return (day) => day.charAt(0);
            case 'short':
                return (day) => day.slice(0, 3);
            case 'long':
                return (day) => day;
            default:
                return undefined;
        }
    }, [weekDayFormat]);
    // SWW specific
    const getCalendarOptions = useMemo(() => ({
        ...defaultDatepickerArgs,
        ...calendarOptions,
        isWinterWonderland,
        monthsShown,
        isAfterNov,
        isAfterDec,
        minDate,
        maxDate,
        usedYear,
        dayClassName: getDayClassName,
        ...(calendarStartDay !== undefined ? { calendarStartDay } : {}),
        ...(formatWeekDay ? { formatWeekDay } : {}),
    }), [
        isWinterWonderland,
        monthsShown,
        isAfterNov,
        isAfterDec,
        minDate,
        maxDate,
        usedYear,
        calendarOptions,
        calendarStartDay,
        formatWeekDay,
        getDayClassName,
    ]);
    const isMobile = useMemo(() => window.innerWidth <= MediaSizes.SM_MAX, [MediaSizes, window.innerWidth]);
    const [currentSwipedMonth, setCurrentSwipedMonth] = useState(1);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const [interacted, setInteracted] = useState(false);
    const offsetModifier = 6;
    const transformAmount = useMemo(() => {
        if (!isMobile) {
            return 0;
        }
        return currentSwipedMonth !== 1 ? -((currentSwipedMonth - 1) * (swwCalendarWidthWithPadding)) : 0;
    }, [isMobile, currentSwipedMonth]);
    const handleDateChange = useCallback((value) => {
        setInteracted(true);
        setCurrentSwipedMonth(Math.min(getCalendarOptions.monthsShown ?? 3, Math.max(1, value)));
    }, [setCurrentSwipedMonth, getCalendarOptions]);
    const handleSwiper = useCallback((next) => {
        if (isMobile) {
            const newValue = next ? currentSwipedMonth + 1 : currentSwipedMonth - 1;
            setInteracted(true);
            setCurrentSwipedMonth(next
                ? Math.min(getCalendarOptions.monthsShown ?? 3, Math.max(1, newValue))
                : Math.max(1, newValue));
        }
        setCalendarHighlighted(false);
    }, [isMobile, setCalendarHighlighted, currentSwipedMonth, getCalendarOptions]);
    const gestures = useGesture({
        onDrag: ({ direction, distance, swipe }) => {
            const horizontal = swipe[0]; // [0] is horizontal [1] is vertical
            // 1 and -1 indicate direction
            if (horizontal !== 0) {
                handleSwiper(horizontal === -1); // on left swipe go next
                setSwipeOffset(0);
            }
            else {
                setSwipeOffset(direction[0] * offsetModifier);
            }
        },
        onDragEnd: (state) => setSwipeOffset(0),
    }, {
        drag: {
            axis: 'x',
            preventScroll: false,
            preventScrollAxis: 'y',
            swipe: {
                distance: 1,
                velocity: 0.001,
                duration: 9999,
            },
        },
    });
    const monthsToDisplay = useMemo(() => {
        if (minDate && maxDate) {
            return eachMonthOfInterval({ start: minDate, end: maxDate }).map((date) => format(date, 'LLLL'));
        }
        return [];
    }, [maxDate, minDate]);
    // End SWW specific
    const renderCalendar = useCallback(() => {
        const calendar = (_jsx(DatePicker, { ...getCalendarOptions, id: fieldName, selected: selectedDate, filterDate: filterDates, openToDate: returnOpenToDate(), calendarClassName: className, renderCustomHeader: renderHeaderContent, renderDayContents: (dayOfMonth, date) => DatepickerDay(dayOfMonth, date), onChange: (date) => (date ? handleChange(date) : false), onMonthChange: handleMonthChanged, peekNextMonth: false }));
        if (layout === 'winter-wonderland' && isMobile) {
            return (_jsx(CalendarWrapper, { ...gestures(), selectedIdx: currentSwipedMonth, style: { transform: `translateX(${transformAmount + swipeOffset}px)` }, ref: ref, id: `${prefix}-wrapper`, className: calendarClasses, children: calendar }));
        }
        return (_jsx(CalendarWrapper, { selectedIdx: currentSwipedMonth, style: { transform: `translateX(${transformAmount + swipeOffset}px)` }, ref: ref, id: `${prefix}-wrapper`, className: calendarClasses, children: calendar }));
    }, [
        layout,
        isMobile,
        gestures,
        currentSwipedMonth,
        transformAmount,
        swipeOffset,
        ref,
        prefix,
        getCalendarOptions,
        fieldName,
        selectedDate,
        filterDates,
        returnOpenToDate,
        className,
        renderHeaderContent,
        DatepickerDay,
        handleMonthChanged,
    ]);
    const calendarAndKey = (_jsxs(_Fragment, { children: [isWorking && requiresVisitorsCheck ? (_jsx(LoadingDates, { ref: loadingRef })) : (_jsxs(_Fragment, { children: [layout === 'winter-wonderland' && isMobile && monthsToDisplay.length > 0 && monthsShown > 1 && !isAfterDec && (_jsx(Timeline, { monthCount: monthsToDisplay.length, style: { transform: `translateX(${transformAmount}px)` }, children: monthsToDisplay.map((month, idx) => (_jsx(TimelineItem, { role: "button", onClick: () => {
                                handleDateChange(idx + 1);
                                setCalendarHighlighted(false);
                            }, active: currentSwipedMonth === idx + 1, idx: idx, children: month }))) })), renderCalendar()] })), (layout === 'winter-wonderland' && monthsToDisplay.length) > 0 && (_jsxs(_Fragment, { children: [_jsx(AvailabilityKey, {}), !isAfterDec && _jsx(SWWArrow, {})] }))] }));
    return (_jsxs(DatesCTX.Provider, { value: {
            availablePrices,
            lowAvailabilityDates,
            highAvailabilityDates,
            averageAvailabilityDates,
            soldOutDates,
            selectedDate,
            showAvailability,
        }, children: [_jsx("input", { type: "hidden", name: fieldName, value: fieldValue ?? '' }), _jsx(CalendarKeyWrapper, { children: calendarAndKey }), _jsx(DatePickerStyles, { id: `${prefix}-wrapper` }, `${prefix}-default-datepicker-styles`)] }));
}
CustomDatepicker.defaultValues = {
    ProductId: undefined,
    DayTicketId: undefined,
    shownDate: undefined,
    requiresVisitorsCheck: false,
    calendarOptions: {},
};
export default CustomDatepicker;
